import { useEffect, useRef, useMemo, useState } from "react"
import { useAiStore } from "@/stores/ai-store"
import { Copy, ThumbsUp, ThumbsDown, RotateCcw, Check } from "lucide-react"
import type { AiMessage } from "@/lib/ai-types"
import { AiStepGroup } from "./ai-step-indicator"
import { RichToolCardRenderer } from "./tool-cards/tool-card-registry"

/**
 * Lightweight markdown renderer matching the rest of the app.
 */
function parseTableRow(line: string): string[] {
  return line.split("|").slice(1, -1).map((c) => c.trim())
}

function isSeparatorRow(line: string): boolean {
  return /^\|[\s\-:|]+\|$/.test(line.trim())
}

function renderTable(tableLines: string[], key: number): React.ReactNode {
  const headerCells = parseTableRow(tableLines[0])
  const dataRows = tableLines.slice(isSeparatorRow(tableLines[1]) ? 2 : 1)

  return (
    <div key={key} className="my-2 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-muted/50">
            {headerCells.map((cell, j) => (
              <th key={j} className="px-3 py-1.5 text-left text-xs font-semibold text-foreground">
                {formatInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.filter((r) => !isSeparatorRow(r)).map((row, j) => (
            <tr key={j} className="border-t border-border">
              {parseTableRow(row).map((cell, k) => (
                <td key={k} className="px-3 py-1.5 text-[var(--text-secondary)]">
                  {formatInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n")
  const elements: React.ReactNode[] = []

  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const key = i

    // Table: collect consecutive | lines
    if (line.trimStart().startsWith("|") && line.trimEnd().endsWith("|")) {
      const tableLines: string[] = []
      while (i < lines.length && lines[i].trimStart().startsWith("|") && lines[i].trimEnd().endsWith("|")) {
        tableLines.push(lines[i])
        i++
      }
      if (tableLines.length >= 2) {
        elements.push(renderTable(tableLines, key))
      } else {
        elements.push(<p key={key} className="leading-relaxed">{formatInline(tableLines[0])}</p>)
      }
      continue
    }

    i++
    if (line.startsWith("### ")) {
      elements.push(<h4 key={key} className="mt-3 mb-1 text-[14px] font-semibold text-foreground">{formatInline(line.slice(4))}</h4>)
    } else if (line.startsWith("## ")) {
      elements.push(<h3 key={key} className="mt-3 mb-1 text-[15px] font-semibold text-foreground">{formatInline(line.slice(3))}</h3>)
    } else if (line.startsWith("# ")) {
      elements.push(<h2 key={key} className="mt-3 mb-1 text-[16px] font-bold text-foreground">{formatInline(line.slice(2))}</h2>)
    } else if (line.match(/^[-*] /)) {
      elements.push(
        <div key={key} className="flex gap-2 pl-1 leading-relaxed">
          <span className="text-[var(--text-tertiary)]">&bull;</span>
          <span>{formatInline(line.slice(2))}</span>
        </div>
      )
    } else if (line.match(/^\d+\. /)) {
      const match = line.match(/^(\d+)\. (.*)/)
      if (match) {
        elements.push(
          <div key={key} className="flex gap-2 pl-1 leading-relaxed">
            <span className="min-w-[16px] tabular-nums text-[var(--text-tertiary)]">{match[1]}.</span>
            <span>{formatInline(match[2])}</span>
          </div>
        )
      }
    } else if (line.trim() === "") {
      elements.push(<div key={key} className="h-1.5" />)
    } else {
      elements.push(<p key={key} className="leading-relaxed">{formatInline(line)}</p>)
    }
  }
  return <>{elements}</>
}

function formatInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    const codeMatch = remaining.match(/^`([^`]+)`/)
    if (codeMatch) {
      parts.push(
        <code
          key={key++}
          className="rounded bg-[var(--bg-surface-raised)] px-1 py-[1px] font-mono text-xs text-[var(--text-secondary)]"
        >
          {codeMatch[1]}
        </code>
      )
      remaining = remaining.slice(codeMatch[0].length)
      continue
    }
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/)
    if (boldMatch) {
      parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>)
      remaining = remaining.slice(boldMatch[0].length)
      continue
    }
    const italicMatch = remaining.match(/^\*(.+?)\*/)
    if (italicMatch) {
      parts.push(<em key={key++}>{italicMatch[1]}</em>)
      remaining = remaining.slice(italicMatch[0].length)
      continue
    }
    const nextSpecial = remaining.search(/[`*]/)
    if (nextSpecial === -1) { parts.push(remaining); break }
    if (nextSpecial === 0) { parts.push(remaining[0]); remaining = remaining.slice(1) }
    else { parts.push(remaining.slice(0, nextSpecial)); remaining = remaining.slice(nextSpecial) }
  }
  return <>{parts}</>
}

/* ── Action row for assistant messages ── */
function MessageActions({ content }: { content: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="mt-2 flex items-center gap-px">
      <button
        onClick={handleCopy}
        className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-muted)] hover:text-[var(--text-secondary)]"
        title="Kopiuj"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      <button
        className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-muted)] hover:text-[var(--text-secondary)]"
        title="Pomocne"
      >
        <ThumbsUp size={14} />
      </button>
      <button
        className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-muted)] hover:text-[var(--text-secondary)]"
        title="Niepomocne"
      >
        <ThumbsDown size={14} />
      </button>
      <button
        className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-muted)] hover:text-[var(--text-secondary)]"
        title="Wygeneruj ponownie"
      >
        <RotateCcw size={14} />
      </button>
    </div>
  )
}

/* ── User message ── */
function UserBubble({ message }: { message: AiMessage }) {
  return (
    <div className="flex justify-end">
      <div className="rounded-[10px] bg-black/[0.04] px-2.5 py-1.5 text-[14px] font-medium leading-[20px] tracking-[-0.01em] text-[var(--text-primary)] dark:bg-white/[0.08]">
        {message.content}
      </div>
    </div>
  )
}

/* ── Assistant message ── */
function AssistantBubble({ message, toolResultsMap }: { message: AiMessage; toolResultsMap: Map<string, string> }) {
  const content = useMemo(() => {
    if (!message.content) return null
    return renderMarkdown(message.content)
  }, [message.content])

  const historicToolCalls = useMemo(() => {
    if (!message.tool_calls?.length) return []
    return message.tool_calls.map((tc) => ({
      id: tc.id,
      name: tc.name,
      status: "done" as const,
      args: tc.arguments || "",
      result: toolResultsMap.get(tc.id) || null,
      startedAt: new Date(message.created_at).getTime(),
      finishedAt: new Date(message.created_at).getTime(),
    }))
  }, [message.tool_calls, message.created_at, toolResultsMap])

  return (
    <div className="max-w-[85%]">
      {historicToolCalls.map((tc) => (
        <RichToolCardRenderer key={tc.id} toolCall={tc} />
      ))}

      {content && (
        <div className="text-[14px] text-[var(--text-primary)]">
          {content}
        </div>
      )}

      {message.status === "error" && (
        <p className="mt-1 text-xs text-red-500">
          Wystąpił błąd podczas przetwarzania.
        </p>
      )}

      {message.content && <MessageActions content={message.content} />}
    </div>
  )
}

/* ── Streaming bubble ── */
function StreamingBubble() {
  const { streamingContent, richToolCalls } = useAiStore()

  const rendered = useMemo(() => {
    if (!streamingContent) return null
    return renderMarkdown(streamingContent)
  }, [streamingContent])

  return (
    <div className="max-w-[85%]">
      {richToolCalls.size > 0 && <AiStepGroup toolCalls={richToolCalls} />}

      {rendered && (
        <div className="text-[14px] text-[var(--text-primary)]">
          {rendered}
        </div>
      )}

      {!streamingContent && richToolCalls.size === 0 && (
        <div className="flex items-center gap-1.5 py-2">
          <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--text-tertiary)] opacity-60" />
          <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--text-tertiary)] opacity-60 [animation-delay:150ms]" />
          <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--text-tertiary)] opacity-60 [animation-delay:300ms]" />
        </div>
      )}
    </div>
  )
}

/* ── Messages container ── */
interface AiFullpageMessagesProps {
  messages: AiMessage[]
}

export function AiFullpageMessages({ messages }: AiFullpageMessagesProps) {
  const { isStreaming, streamingContent, richToolCalls } = useAiStore()
  const bottomRef = useRef<HTMLDivElement>(null)

  // Build a map of tool_call_id → result from tool messages
  const toolResultsMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const msg of messages) {
      if (msg.role === "tool" && msg.tool_results) {
        for (const tr of msg.tool_results) {
          map.set(tr.tool_call_id, tr.result)
        }
      }
    }
    return map
  }, [messages])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages.length, streamingContent, richToolCalls.size])

  return (
    <div className="flex flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[680px] space-y-6 px-6 py-6">
        {messages.map((msg) => {
          if (msg.role === "tool") return null
          if (msg.role === "user") return <UserBubble key={msg.id} message={msg} />
          // Skip tool-only assistant messages (intermediate steps) — the final answer is in a separate message
          if (msg.role === "assistant" && msg.tool_calls?.length && !msg.content) return null
          return <AssistantBubble key={msg.id} message={msg} toolResultsMap={toolResultsMap} />
        })}

        {isStreaming && <StreamingBubble />}

        <div ref={bottomRef} />
      </div>
    </div>
  )
}
